import React from 'react';
import { useIntl } from 'react-intl';

import type { DirectoryUser } from '../../hooks/useChatUserDirectory';
import UserPreviewItem from './UserPreviewItem';
import { EmptyChatsText, SectionHeader, UserDirectoryRoot } from './StyledComponents';

interface UserDirectorySectionProps {
  users: ReadonlyArray<DirectoryUser>;
  onStartChat: (user: DirectoryUser) => void;
  /** True while a chat is being created — disables all rows (interaction lock). */
  isCreating: boolean;
}

/**
 * The "Users" directory section rendered beneath the chat list (DA-19646). Always renders the
 * "Users" header when mounted; when there are no eligible users a centered "No users" placeholder
 * is shown beneath the header, styled identically to the "No chats" placeholder (reuses
 * EmptyChatsText, same icon-free text + bottom divider) — spec: "Empty user directory". Render
 * gating on full channel load and context-filter visibility is owned by the parent (ChannelList).
 */
const UserDirectorySection: React.FC<UserDirectorySectionProps> = ({ users, onStartChat, isCreating }) => {
  const { formatMessage } = useIntl();
  return (
    <UserDirectoryRoot>
      <SectionHeader>{formatMessage({ id: 'chat.channelList.sectionUsers', defaultMessage: 'Users' })}</SectionHeader>
      {users.length === 0 ? (
        <EmptyChatsText>{formatMessage({ id: 'chat.channelList.noUsers', defaultMessage: 'No users' })}</EmptyChatsText>
      ) : (
        users.map((user) => (
          <UserPreviewItem key={user.userGUID} user={user} onClick={onStartChat} disabled={isCreating} />
        ))
      )}
    </UserDirectoryRoot>
  );
};

export default UserDirectorySection;
